import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  ComponentRef,
  Inject,
  Injectable,
  Injector,
  NgModuleRef,
  Optional,
  Renderer2,
  RendererFactory2,
  TemplateRef,
  ViewContainerRef,
  createNgModule
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';

import { Observable, ReplaySubject, Subject, delay, distinctUntilChanged, filter, map, startWith } from 'rxjs';

import {
  HDMS_SCOPE_CLASSES,
  MessageCopy,
  MessageType,
  Toast,
  ToastMessagesFactory,
  ToastOptions
} from '@hdms/shared/common-types';
import { CssUtils, WindowRefService, jsonEqual, sanitizeUrl } from '@hdms/shared/util-core';

import { ToastMessagesPosition } from './toast-message-positions.enum';
// Must only be used as a type!
// Otherwise, it'll be imported and lazy-loading ToastModule is for the toilet
import { ToastMessagesContainerComponent as ToastMessagesContainerComponentType } from './toast-messages-container.component';

const DEFAULT_TIMEOUT = 3000;

@Injectable({
  providedIn: 'root'
})
export class ToastMessagesService implements ToastMessagesFactory {
  public globalToastContainerRef: ViewContainerRef;
  public globalToastContainerElement: HTMLElement;
  public toastDismissed = new Subject<number>();

  private readonly toasts$ = new ReplaySubject<Array<Toast>>(1);
  private readonly toastMessageContainerInstancesSet = new Set<number>();
  private readonly renderer: Renderer2;
  private toastsArray: Toast[] = [];
  private index = 0;
  private globalToastContainerComponentRef: ComponentRef<ToastMessagesContainerComponentType>;
  private initialized = false;

  private static getHostClassNames(): string {
    return CssUtils.getJoinedClassList(
      ToastMessagesPosition.BOTTOM_LEFT,
      'position-absolute',
      'shrd-uic-toast-messages'
    );
  }

  constructor(
    private readonly rendererFactory: RendererFactory2,
    private readonly injector: Injector,
    private readonly appRef: ApplicationRef,
    private readonly winRef: WindowRefService,
    @Optional() private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.renderer = rendererFactory?.createRenderer(null, null);
    this.subscribeToRouterEvents();
  }

  public getToasts$(): Observable<Toast[]> {
    return this.toasts$.asObservable();
  }

  /**
   * @param  {MessageCopy} message
   * @param  {string} title?
   * @param  {ToastOptions} options?
   * @returns number : ToastID
   */
  public info(message: MessageCopy, title?: string, options?: ToastOptions): number {
    return this.createToast(MessageType.INFO, message, title, options);
  }

  /**
   * @param  {MessageCopy} message
   * @param  {string} title?
   * @param  {ToastOptions} options?
   * @returns number : ToastID
   */
  public success(message: MessageCopy, title?: string, options?: ToastOptions): number {
    return this.createToast(MessageType.SUCCESS, message, title, options);
  }

  /**
   * @param  {MessageCopy} message
   * @param  {string} title?
   * @param  {ToastOptions} options?
   * @returns number : ToastID
   */
  public warn(message: MessageCopy, title?: string, options?: ToastOptions): number {
    return this.createToast(MessageType.WARNING, message, title, options);
  }

  /**
   * @param  {MessageCopy} message
   * @param  {string} title?
   * @param  {ToastOptions} options?
   * @returns number : ToastID
   */
  public danger(message: MessageCopy, title?: string, options?: ToastOptions): number {
    return this.createToast(MessageType.DANGER, message, title, options);
  }

  /**
   * @param  {MessageCopy} message
   * @param  {string} title?
   * @param  {ToastOptions} options?
   * @returns number : ToastID
   */
  public email(message: MessageCopy, title?: string, options?: ToastOptions): number {
    return this.createToast(MessageType.EMAIL, message, title, options);
  }

  /**
   * Remove a custom toast by id or all toasts if empty param
   * @param  {number} toastId?
   * @returns void
   */
  public clear(toastId?: number): void {
    if (toastId) {
      this.removeToast(toastId);
    } else {
      this.toastsArray = this.toastsArray.filter(toast => toast.options.displayPermanently);
    }
    this.updateToasts();
  }

  public registerToastContainer(id: number): void {
    this.toastMessageContainerInstancesSet.add(id);
  }

  public unregisterToastContainer(id: number): void {
    this.toastMessageContainerInstancesSet.delete(id);
  }

  public createToast(
    type: MessageType = MessageType.INFO,
    message: MessageCopy = '',
    title = '',
    options: ToastOptions = { tapToDismiss: true, autoClose: false, displayPermanently: false }
  ): number {
    this.createToastContainer();
    this.index++;

    const instance: Toast = {
      id: this.index,
      type,
      title,
      message,
      options: {
        tapToDismiss: !!options.tapToDismiss,
        autoClose: !!options.autoClose,
        timeout: options.timeout || DEFAULT_TIMEOUT,
        showCloseButton: !!options.showCloseButton,
        dataQaId: options.dataQaId,
        uniqueKey: options.uniqueKey,
        displayPermanently: !!options.displayPermanently
      }
    };

    if (!this.checkExistingToast(instance)) {
      this.toastsArray.push(instance);
      this.updateToasts();
    }

    return instance.id;
  }

  private clearOnRouteChange(): void {
    this.toastsArray = [];
    this.updateToasts();
  }

  /**
   * Check if toast should be unique and in case if there exists already one.
   * In case, if new toast props have changed, update existing toast and
   * emit the change.
   * @param instance
   * @private
   */
  private checkExistingToast(instance: Toast): Toast {
    const { uniqueKey } = instance.options;
    const existingToast = uniqueKey && this.toastsArray.find(toast => uniqueKey === toast.options.uniqueKey);

    if (existingToast) {
      instance.id = existingToast.id;
      if (
        instance.message instanceof TemplateRef ||
        existingToast.message instanceof TemplateRef ||
        !jsonEqual(instance, existingToast)
      ) {
        // if data has changed, update existing toast
        Object.keys(instance).forEach(key => {
          existingToast[key] = instance[key];
        });
        this.updateToasts();
      }
    }
    return existingToast;
  }

  private hasFeatureLevelToastContainer(): boolean {
    return this.toastMessageContainerInstancesSet.size > 0;
  }

  private subscribeToRouterEvents(): void {
    this.router?.events
      ?.pipe(
        filter(event => event instanceof NavigationStart),
        map((event: NavigationStart) => sanitizeUrl(event.url)),
        startWith(this.winRef.nativeWindow.location.pathname),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.removeContainerComponent();
        this.clearOnRouteChange();
      });
  }

  private updateToasts(): void {
    this.toasts$.next(this.toastsArray);
  }

  private removeToast(toastId: number): void {
    this.toastsArray = this.toastsArray.filter(toast => toast.id !== toastId);
    this.toastDismissed.next(toastId);
  }

  private createToastContainer(): void {
    if (this.hasFeatureLevelToastContainer()) {
      // remove global toast message if there's any
      this.removeContainerComponent();
    } else {
      // add global toast container if no toast container found on page.
      this.renderContainerComponent();
    }
  }

  private async renderContainerComponent(): Promise<void> {
    if (
      this.initialized ||
      this.globalToastContainerComponentRef ||
      !(this.globalToastContainerRef || this.globalToastContainerElement)
    ) {
      return;
    }

    // avoid duplicated creation of global toast container
    this.initialized = true;

    const { ToastMessagesModule, ToastMessagesContainerComponent } = await import('./toast-messages.module');
    const ngModuleRef = createNgModule(ToastMessagesModule, this.injector);
    this.globalToastContainerComponentRef = this.globalToastContainerRef
      ? this.createToastContainerComponent(ngModuleRef, ToastMessagesContainerComponent)
      : this.createToastContainerPortal(ngModuleRef, ToastMessagesContainerComponent);

    const toastContainerEle = this.globalToastContainerComponentRef.location.nativeElement;
    const containerClass = ToastMessagesService.getHostClassNames().split(' ');
    containerClass.forEach(el => this.renderer?.addClass(toastContainerEle, el));
    this.globalToastContainerComponentRef.instance.messageContainer = this.globalToastContainerRef
      ? toastContainerEle.parentElement
      : this.globalToastContainerElement;
    // make sure toast displays after the container is well positioned with floating calculation
    this.globalToastContainerComponentRef.instance.toasts$ = this.toasts$.pipe(delay(0));
  }

  private createToastContainerComponent(
    ngModuleRef: NgModuleRef<unknown>,
    component: typeof ToastMessagesContainerComponentType
  ): ComponentRef<ToastMessagesContainerComponentType> {
    return this.globalToastContainerRef.createComponent(component, {
      ngModuleRef
    });
  }

  private createToastContainerPortal(
    ngModuleRef: NgModuleRef<unknown>,
    component: typeof ToastMessagesContainerComponentType
  ): ComponentRef<ToastMessagesContainerComponentType> {
    const toastContainer = this.document.createElement('div');
    toastContainer.classList.add(...HDMS_SCOPE_CLASSES);
    this.globalToastContainerElement.appendChild(toastContainer);
    const portalOutlet = new DomPortalOutlet(toastContainer, ngModuleRef.componentFactoryResolver, this.appRef);
    return portalOutlet.attachComponentPortal(new ComponentPortal(component));
  }

  private removeContainerComponent(): void {
    this.initialized = false;
    this.globalToastContainerComponentRef?.destroy();
    this.globalToastContainerComponentRef = null;
    this.globalToastContainerRef?.clear();
  }
}
